import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Product } from '@prody/shared';
import {
  Button, Code, DataTable, EmptyState, IconPlus, Input, ListPane,
  PageHeader, Stamp, Toolbar, Tooltip, vars, Workbench, type Column,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useProducts } from '~/platform/data/queries';
import { productName } from '~/platform/domain/presentation';

/* ==================================================================
   Product manager — the write side.

   A table, not cards: this is where somebody sets a hundred tax
   codes and minimum quantities, and cards would triple the scroll.
   The trait columns only render when the tenant's trait pack turns
   them on, which is what keeps the model from being welded to food.
   ================================================================== */

export default function ProductsPage() {
  const { t, i18n } = useTranslation(['products', 'catalog', 'common']);
  const { business, format } = useBusiness();
  const [search, setSearch] = useState('');
  const { data: products = [], isLoading } = useProducts(business.id, search ? { search } : undefined);

  const locale = i18n.resolvedLanguage ?? 'en';
  const taxLabel = useMemo(
    () => new Map(business.settings.taxScheme.rates.map((r) => [r.key, r.label])),
    [business.settings.taxScheme],
  );

  /* Only show trait columns the tenant actually uses. A business
     selling packaged goods never sees "Calibre". */
  const usesGrading = products.some((p) => p.traits.grading?.calibre);
  const usesPerishable = products.some((p) => p.traits.perishable);

  const columns = useMemo<Column<Product>[]>(() => {
    const base: Column<Product>[] = [
      {
        key: 'sku',
        header: t('common:labels.sku'),
        width: '130px',
        sortable: true,
        sortValue: (p) => p.sku,
        cell: (p) => <Code>{p.sku}</Code>,
      },
      {
        key: 'name',
        header: t('common:labels.product'),
        width: 'minmax(180px, 2fr)',
        sortable: true,
        sortValue: (p) => productName(p, locale),
        cell: (p) => <span className="truncate">{productName(p, locale)}</span>,
      },
      {
        key: 'unit',
        header: t('common:labels.unit'),
        width: '128px',
        hideBelow: 'sm',
        cell: (p) => (
          <span className="row" style={vars({ '--gap': 'var(--s-3)' })}>
            <span className="num">{format.quantity({ value: p.quantityStep, unit: p.salesUnit })}</span>
            {p.traits.catchWeight ? (
              /* Wrapped in a span because Radix's trigger needs a real
                 DOM node to measure; Stamp does not forward a ref. */
              <Tooltip content={t('catalog:catchWeightHint')}>
                <span>
                  <Stamp tone="info" size="sm">{t('catalog:catchWeight')}</Stamp>
                </span>
              </Tooltip>
            ) : null}
          </span>
        ),
      },
    ];

    if (usesGrading) {
      base.push({
        key: 'calibre',
        header: t('catalog:calibre'),
        width: '90px',
        hideBelow: 'lg',
        cell: (p) => <span className="num">{p.traits.grading?.calibre ?? '—'}</span>,
      });
    }

    if (usesPerishable) {
      base.push({
        key: 'shelf',
        header: t('catalog:shelfLife'),
        align: 'num',
        width: '92px',
        hideBelow: 'md',
        sortable: true,
        sortValue: (p) => p.traits.perishable?.shelfLifeDays ?? 0,
        cell: (p) =>
          p.traits.perishable ? (
            <span className="num">{p.traits.perishable.shelfLifeDays} d</span>
          ) : (
            <span className="faint">—</span>
          ),
      });
    }

    base.push(
      {
        key: 'tax',
        header: t('common:labels.tax'),
        width: '150px',
        hideBelow: 'lg',
        cell: (p) => <span className="t-small dim">{taxLabel.get(p.taxRateKey) ?? p.taxRateKey}</span>,
      },
      {
        key: 'subs',
        header: t('catalog:substitutes'),
        align: 'num',
        width: '80px',
        hideBelow: 'lg',
        cell: (p) =>
          p.substituteIds.length ? (
            <Tooltip content={t('catalog:substitutesHint')}>
              <span className="num">{p.substituteIds.length}</span>
            </Tooltip>
          ) : (
            <span className="faint">—</span>
          ),
      },
      {
        key: 'status',
        header: t('common:labels.status'),
        width: '128px',
        cell: (p) => (
          <Stamp
            tone={p.status === 'active' ? 'ok' : p.status === 'draft' ? 'neutral' : 'warn'}
            weight={p.status === 'discontinued' ? 'solid' : 'outline'}
          >
            {t(`catalog:status.${p.status}`)}
          </Stamp>
        ),
      },
    );

    return base;
  }, [t, locale, format, taxLabel, usesGrading, usesPerishable]);

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('catalog:eyebrow')}
        title={t('title')}
        count={isLoading ? undefined : t('common:labels.results', { count: products.length })}
        actions={
          <Button variant="primary" icon={<IconPlus />}>
            {t('newProduct')}
          </Button>
        }
      />

      <Toolbar>
        <Input
          type="search"
          placeholder={t('common:actions.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ inlineSize: 260 }}
        />
      </Toolbar>

      <ListPane>
        <DataTable<Product>
          rows={products}
          columns={columns}
          rowKey={(p) => p.id}
          rowMeta={(p) => ({
            handlingClass: p.handlingClass,
            struck: p.status === 'discontinued',
          })}
          loading={isLoading}
          caption={t('title')}
          empty={<EmptyState title={t('catalog:empty')} body={t('catalog:emptyBody')} />}
        />
      </ListPane>
    </Workbench>
  );
}
